import React, { useState, useEffect } from "react";
import { DollarSign, TrendingUp, TrendingDown, Calendar, Server, Database, Globe, Briefcase } from "lucide-react";

const CostManagementCards = ({ staticCostData, budgetAccountData = [], epNoOemData = [] }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };
  const [monthNames, setMonthNames] = useState({ currentMonth: '', previousMonth: '', lastPreviosMonth: '' });
  const [animatedCurrentCost, setAnimatedCurrentCost] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  useEffect(() => {
    // Calculate IST offset (5.5 hours in milliseconds)
    const offset = 5.5 * 60 * 60 * 1000;

    // Get current date with IST offset
    const currentDate = new Date(new Date().getTime() + offset);
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    // Format the current month name
    const currentMonthName = new Date(currentYear, currentMonth).toLocaleString('default', { month: 'long' });

    // Get previous month
    const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const prevYear = currentMonth === 0 ? currentYear - 1 : currentYear;
    const previousMonthName = new Date(prevYear, prevMonth).toLocaleString('default', { month: 'long' });

    // Get month before previous month
    const lastPrevMonth = prevMonth === 0 ? 11 : prevMonth - 1;
    const lastPrevYear = prevMonth === 0 ? prevYear - 1 : prevYear;
    const lastPreviousMonthName = new Date(lastPrevYear, lastPrevMonth).toLocaleString('default', { month: 'long' });

    // Set all month names
    setMonthNames({
      currentMonth: currentMonthName,
      previousMonth: previousMonthName,
      lastPreviousMonth: lastPreviousMonthName
    });
  }, []);

  const currentMonthCost = staticCostData.currentMonthCost || 0;
  const previousMonthCost = staticCostData.previousMonthTotalCost || 0;
  const percentageDiff = staticCostData.totalIncreasePercentage || 0;
  const isIncrease = percentageDiff > 0;
  const forecastCost = staticCostData.forecastedCost || 0;

  // Top services data from API
  const topService = {
    name: staticCostData.topService || "Unknown",
    cost: staticCostData.topServiceCost || 0,
  };
  const topAccount = {
    name: staticCostData.topAccountName || "Unknown",
    cost: staticCostData.topAccountCost || 0,
  };
  const topRegion = {
    name: staticCostData.topRegion || "Unknown",
    cost: staticCostData.topRegionCost || 0,
  };
  const topProject = {
    name: staticCostData.topEpNo || "Unknown",
    cost: staticCostData.topEpNoCost || 0,
    oem:
      epNoOemData.find((item) => item.EpNo === staticCostData.topEpNo)?.Oem ||
      "N/A",
  };
  // Process budget data from budgetAccountData
  const overBudgetAccounts = budgetAccountData.filter(account => account.currentCost > account.Budget);
  const withinBudgetAccounts = budgetAccountData.filter(account => account.currentCost <= account.Budget);

  // Find top exceeding and top under budget accounts
  const sortedOverBudget = [...overBudgetAccounts].sort((a, b) =>
    (b.currentCost / b.Budget) - (a.currentCost / a.Budget)
  );

  const sortedUnderBudget = [...withinBudgetAccounts]
    .filter(account => account.Budget > 0) // Avoid division by zero
    .sort((a, b) =>
      (a.currentCost / a.Budget) - (b.currentCost / b.Budget)
    );

  const topExceeding = sortedOverBudget.length > 0 ? {
    name: sortedOverBudget[0].Name || sortedOverBudget[0].AccountName || "Unknown",
    budget: sortedOverBudget[0].Budget,
    spent: sortedOverBudget[0].currentCost,
    percentage: Math.round(((sortedOverBudget[0].currentCost / sortedOverBudget[0].Budget) - 1) * 100)
  } : null;

  const topUnder = sortedUnderBudget.length > 0 ? {
    name: sortedUnderBudget[0].Name || sortedUnderBudget[0].AccountName || "Unknown",
    budget: sortedUnderBudget[0].Budget,
    spent: sortedUnderBudget[0].currentCost,
    percentage: Math.round(100 - (sortedUnderBudget[0].currentCost / sortedUnderBudget[0].Budget * 100))
  } : null;

  // Primary color and derivatives
  const primaryColor = "#003366";
  const primaryLight = "#E5EBF0"; // Very light version for backgrounds
  const primaryLighter = "#CCd7E0"; // Light version for borders
  const primaryMedium = "#336699"; // Medium version 
  const primaryDark = "#002244"; // Darker version

  // Animated counter effect
  useEffect(() => {
    if (currentMonthCost > 0 && !hasAnimated) {
      setHasAnimated(true);
      let startTime;
      const duration = 1500;
      const startValue = 0;
      const endValue = currentMonthCost;

      const animation = (currentTime) => {
        if (!startTime) startTime = currentTime;
        const progress = Math.min((currentTime - startTime) / duration, 1);

        const currentCount = startValue + progress * (endValue - startValue);
        setAnimatedCurrentCost(currentCount);

        if (progress < 1) {
          requestAnimationFrame(animation);
        }
      };

      requestAnimationFrame(animation);
    } else if (currentMonthCost === 0) {
      setAnimatedCurrentCost(0);
    }
  }, [currentMonthCost, hasAnimated]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
      {/* Card 1: Current Month, Previous Month, and Forecast - MODIFIED */}
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6 overflow-hidden relative h-full">
        <div className="absolute top-0 right-0 w-24 h-24 bg-blue-50 rounded-bl-full -mr-8 -mt-8 z-0" style={{ backgroundColor: primaryLight }}></div>

        <h3 className="text-lg font-bold mb-4 relative z-10" style={{ color: primaryColor }}>Cost Overview</h3>

        <div className="space-y-4 relative z-10">
          {/* Current Month Cost - Featured Card */}
          <div className="border rounded-lg p-3 shadow-sm mb-4" style={{ borderColor: primaryLighter, backgroundColor: primaryLight }}>
            <div className="flex justify-between items-start mb-1">
              <div className="flex items-center">
                <div className="p-1 rounded-full mr-2" style={{ backgroundColor: 'white' }}>
                  <DollarSign className="h-4 w-4" style={{ color: primaryColor }} />
                </div>
                <p className="text-sm font-medium text-gray-600">Current Month</p>
              </div>
              <div className="text-xs text-gray-500">{monthNames.currentMonth}</div>
            </div>
            <p className="text-2xl font-bold text-center my-1" style={{ color: primaryDark }}>
              {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD'
              }).format(animatedCurrentCost)}
            </p>

            {/* Mini progress bar */}
            <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
              <div className="h-2 rounded-full"
                style={{
                  width: `${Math.min(100, (currentMonthCost / (forecastCost * 1.2)) * 100)}%`,
                  backgroundColor: primaryColor
                }}></div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {/* Previous Month Cost - MODIFIED to show trend vs Jan */}
            <div className="border rounded-lg p-3 bg-white shadow-sm" style={{ borderColor: primaryLighter }}>
              <div className="flex items-center mb-1">
                <Calendar className="h-4 w-4 mr-2" style={{ color: primaryMedium }} />
                <p className="text-xs font-medium text-gray-500">Previous Month</p>
              </div>
              <p className="text-lg font-semibold truncate" style={{ color: primaryDark }}>{formatCurrency(previousMonthCost)}</p>
              <div className={`flex items-center text-xs mt-1 font-medium`}
                style={{ color: isIncrease ? '#B22222' : '#006633' }}>
                {isIncrease ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
                {Math.abs(percentageDiff).toFixed(1)}% vs.  {monthNames.lastPreviousMonth}
              </div>
            </div>

            {/* Forecast Cost */}
            <div className="border rounded-lg p-3 bg-white shadow-sm" style={{ borderColor: primaryLighter }}>
              <div className="flex items-center mb-1">
                <TrendingUp className="h-4 w-4 mr-2" style={{ color: primaryColor }} />
                <p className="text-xs font-medium text-gray-500">Forecast</p>
              </div>
              <p className="text-lg font-semibold truncate" style={{ color: primaryDark }}>{formatCurrency(forecastCost)}</p>
              <p className="text-xs text-gray-600">Projected</p>
            </div>
          </div>

          {/* Daily average cost indicator */}
          <div className="mt-2 pt-2 border-t" style={{ borderColor: primaryLighter }}>
            <div className="flex justify-between items-center">
              <p className="text-xs text-gray-500">Daily Average</p>
              <p className="text-sm font-medium" style={{ color: primaryDark }}>
                {formatCurrency(currentMonthCost / (() => {
                  const offset = 5.5 * 60 * 60 * 1000;
                  const currentDate = new Date(new Date().getTime() + offset);
                  return currentDate.getDate();
                })())}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Card 2: Top Service, Account, Region, Project */}
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6 overflow-hidden relative h-full">
        <div className="absolute top-0 right-0 w-24 h-24 rounded-bl-full -mr-8 -mt-8 z-0" style={{ backgroundColor: primaryLight }}></div>

        <h3 className="text-lg font-bold mb-4 relative z-10" style={{ color: primaryColor }}>Top Spenders</h3>

        <div className="grid grid-cols-2 gap-4 relative z-10">
          {/* Top Service */}
          <div className="border rounded-lg p-3 bg-white shadow-sm" style={{ borderColor: primaryLighter }}>
            <div className="flex items-center mb-1">
              <Server className="h-4 w-4 mr-2" style={{ color: primaryColor }} />
              <p className="text-xs font-medium text-gray-500">Top Service</p>
            </div>
            <p className="font-semibold truncate text-lg" style={{ color: primaryDark }}>{topService.name}</p>
            <p className="text-xs text-gray-600 mt-1">{formatCurrency(topService.cost)}</p>
          </div>

          {/* Top Account */}
          <div className="border rounded-lg p-3 bg-white shadow-sm" style={{ borderColor: primaryLighter }}>
            <div className="flex items-center mb-1">
              <Database className="h-4 w-4 mr-2" style={{ color: primaryColor }} />
              <p className="text-xs font-medium text-gray-500">Top Account</p>
            </div>
            <p
              className="font-semibold truncate text-lg"
              style={{ color: primaryDark }}
              title={topAccount.name}
            >
              {topAccount.name}
            </p>
            <p className="text-xs text-gray-600 mt-1">{formatCurrency(topAccount.cost)}</p>
          </div>

          {/* Top Region */}
          <div className="border rounded-lg p-3 bg-white shadow-sm" style={{ borderColor: primaryLighter }}>
            <div className="flex items-center mb-1">
              <Globe className="h-4 w-4 mr-2" style={{ color: primaryColor }} />
              <p className="text-xs font-medium text-gray-500">Top Region</p>
            </div>
            <p className="font-semibold truncate text-lg" style={{ color: primaryDark }}>{topRegion.name}</p>
            <p className="text-xs text-gray-600 mt-1">{formatCurrency(topRegion.cost)}</p>
          </div>

          {/* Top Project */}
          <div className="border rounded-lg p-3 bg-white shadow-sm" style={{ borderColor: primaryLighter }}>
            <div className="flex items-center mb-1">
              <Briefcase className="h-4 w-4 mr-2" style={{ color: primaryColor }} />
              <p className="text-xs font-medium text-gray-500">Top Project</p>
            </div>
            <div className="flex items-baseline">
              <p className="font-semibold truncate text-lg mr-2" style={{ color: primaryDark }}>
                {topProject.name}
              </p>
              <p className="text-xs text-gray-500">
                ( {topProject.oem} )
              </p>
            </div>
            <p className="text-xs text-gray-600 mt-1">
              {formatCurrency(topProject.cost)}
            </p>
          </div>
        </div>

        <div className="mt-6 pt-2 border-t text-xs text-gray-500 relative z-10" style={{ borderColor: primaryLighter }}>
          <p className="text-center">Based on previous month usage</p>
        </div>
      </div>

      {/* Card 3: Budget Tracker */}
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6 overflow-hidden relative h-full">
        <div className="absolute top-0 right-0 w-24 h-24 rounded-bl-full -mr-8 -mt-8 z-0" style={{ backgroundColor: primaryLight }}></div>

        <h3 className="text-lg font-bold mb-0 relative z-10" style={{ color: primaryColor }}>Account Budget Tracker</h3>
        <p className="text-xs text-gray-500 mb-4 relative z-10">(Current Month)</p>
        <div className="space-y-4 relative z-10">
          {/* Budget Status Summary */}
          <div className="flex justify-between mb-2">
            <div className="text-center px-3 py-2 rounded-lg" style={{ backgroundColor: '#F0E5E5' }}>
              <p className="text-sm font-medium text-gray-500">Over Budget</p>
              <p className="text-2xl font-bold" style={{ color: '#993333' }}>{overBudgetAccounts.length}</p>
              <p className="text-xs text-gray-500">accounts</p>
            </div>

            <div className="text-center px-3 py-2 rounded-lg" style={{ backgroundColor: '#E5F0E5' }}>
              <p className="text-sm font-medium text-gray-500">Within Budget</p>
              <p className="text-2xl font-bold" style={{ color: '#336633' }}>{withinBudgetAccounts.length}</p>
              <p className="text-xs text-gray-500">accounts</p>
            </div>

            <div className="text-center px-3 py-2 rounded-lg" style={{ backgroundColor: primaryLight }}>
              <p className="text-sm font-medium text-gray-500">Total</p>
              <p className="text-2xl font-bold" style={{ color: primaryColor }}>
                {budgetAccountData.length}
              </p>
              <p className="text-xs text-gray-500">accounts</p>
            </div>
          </div>

          {/* Top Accounts by Budget Status */}
          <div className="space-y-3 mt-4">
            {/* Account exceeding budget the most */}
            {topExceeding ? (
              <div className="flex items-center p-2 rounded-lg border" style={{ backgroundColor: '#F0E5E5', borderColor: '#E0D0D0' }}>
                <div className="flex-shrink-0 mr-3">
                  <div className="p-2 rounded-full" style={{ backgroundColor: '#F5E0E0' }}>
                    <TrendingUp className="h-4 w-4" style={{ color: '#993333' }} />
                  </div>
                </div>
                <div className="flex-grow min-w-0">
                  <div className="flex justify-between items-baseline">
                    <p className="text-sm font-medium text-gray-700 truncate">{topExceeding.name}</p>
                    <p className="text-xs whitespace-nowrap" style={{ color: '#993333' }}>+{topExceeding.percentage}% over</p>
                  </div>
                  <div className="flex justify-between items-baseline">
                    <p className="text-xs text-gray-500">Budget: {formatCurrency(topExceeding.budget)}</p>
                    <p className="text-xs font-medium text-gray-700">Spent: {formatCurrency(topExceeding.spent)}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-2 rounded-lg border text-center" style={{ backgroundColor: '#F0E5E5', borderColor: '#E0D0D0' }}>
                <p className="text-sm text-gray-600">No accounts over budget</p>
              </div>
            )}

            {/* Account with most remaining budget */}
            {topUnder ? (
              <div className="flex items-center p-2 rounded-lg border" style={{ backgroundColor: '#E5F0E5', borderColor: '#D0E0D0' }}>
                <div className="flex-shrink-0 mr-3">
                  <div className="p-2 rounded-full" style={{ backgroundColor: '#E0F0E0' }}>
                    <TrendingDown className="h-4 w-4" style={{ color: '#336633' }} />
                  </div>
                </div>
                <div className="flex-grow min-w-0">
                  <div className="flex justify-between items-baseline">
                    <p className="text-sm font-medium text-gray-700 truncate">{topUnder.name}</p>
                    <p className="text-xs whitespace-nowrap" style={{ color: '#336633' }}>{topUnder.percentage}% under</p>
                  </div>
                  <div className="flex justify-between items-baseline">
                    <p className="text-xs text-gray-500">Budget: {formatCurrency(topUnder.budget)}</p>
                    <p className="text-xs font-medium text-gray-700">Spent: {formatCurrency(topUnder.spent)}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-2 rounded-lg border text-center" style={{ backgroundColor: '#E5F0E5', borderColor: '#D0E0D0' }}>
                <p className="text-sm text-gray-600">No accounts under budget</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CostManagementCards;